/**
 * URL Management Utilities
 * Provides centralized URL generation, parsing, and navigation helpers
 */

// Map of page names to URL paths
export const PAGE_ROUTES = {
  feedback: "/feedback",
  projects: "/projects",
  "project-setup": "/project-setup",
  "subitem-templates": "/subitem-templates",
  "subitem-templates-new": "/subitem-templates-new",
  "project-emails": "/project-emails",
  clients: "/clients",
  contacts: "/contacts",
  "contacts-demo": "/contacts-demo",
  discrepancies: "/discrepancies",
  takeoffs: "/takeoffs",
  "operations-dashboard": "/operations-dashboard",
  "model-building-manager": "/model-building-manager",
  "planner-config": "/planner-config",
  "manager-dashboard": "/manager-dashboard",
  "office-admin": "/office-admin",
  invoicing: "/invoicing",
  pricing: "/pricing",
  payroll: "/payroll",
  "remote-support": "/remote-support",
  "on-hold-board": "/on-hold-board",
  admin: "/admin",
  employees: "/employees",
  stats: "/stats",
  "profit-analysis": "/profit-analysis",
  "kpi-board": "/kpi-board",
};

// Reverse mapping for converting paths back to page names
export const ROUTE_PAGES = Object.fromEntries(
  Object.entries(PAGE_ROUTES).map(([page, route]) => [route, page]),
);

// Valid project tabs - must match all tabs from tabConfigs.js
export const PROJECT_TABS = [
  "startup",
  "data-prep",
  "utilities",
  "ready-to-model",
  "modeling",
  "double-check",
  "corrections",
  "review",
  "ready-to-export",
  "send",
  "completed",
];

// Valid model building manager tabs
export const MODEL_BUILDING_MANAGER_TABS = [
  "action-items",
  "on-hold",
  "assignment-rules",
];

/**
 * Generate a URL for a specific page
 */
export const generatePageUrl = (page) => {
  return PAGE_ROUTES[page] || "/projects";
};

/**
 * Generate a URL for a project tab
 */
export const generateProjectTabUrl = (tab = "startup") => {
  // Validate tab
  if (!PROJECT_TABS.includes(tab)) {
    tab = "startup";
  }
  return `/projects/${tab}`;
};

/**
 * Generate a URL for a project with modal open
 */
export const generateProjectModalUrl = (tab = "startup", projectId) => {
  if (!projectId) {
    return generateProjectTabUrl(tab);
  }

  // Validate tab
  if (!PROJECT_TABS.includes(tab)) {
    tab = "startup";
  }

  return `/projects/${tab}/${projectId}`;
};

/**
 * Generate a URL for a discrepancy
 */
export const generateDiscrepancyUrl = (discrepancyId = null) => {
  if (!discrepancyId) {
    return "/discrepancies";
  }
  return `/discrepancies/${discrepancyId}`;
};

/**
 * Generate a URL for a project email
 */
export const generateProjectEmailUrl = (emailId = null) => {
  if (!emailId) {
    return "/project-emails";
  }
  return `/project-emails/${emailId}`;
};

/**
 * Generate a URL for a model building manager tab
 */
export const generateModelBuildingManagerUrl = (tab = "action-items") => {
  // Validate tab
  if (!MODEL_BUILDING_MANAGER_TABS.includes(tab)) {
    tab = "action-items";
  }
  return `/model-building-manager/${tab}`;
};

/**
 * Parse the current URL to extract page, tab, and ID information
 */
export const parseCurrentUrl = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { page: "projects", tab: "startup", id: null };
  }

  const [page, tabOrId, id] = segments;

  // Handle projects routes
  if (page === "projects") {
    if (!tabOrId) {
      return { page: "projects", tab: "startup", id: null };
    }

    // Check if tabOrId is a valid tab
    if (PROJECT_TABS.includes(tabOrId)) {
      return { page: "projects", tab: tabOrId, id: id || null };
    } else {
      // tabOrId might be a project ID in old format
      return { page: "projects", tab: "startup", id: tabOrId };
    }
  }

  // Handle discrepancies routes
  if (page === "discrepancies") {
    return { page: "discrepancies", tab: null, id: tabOrId || null };
  }

  // Handle project-emails routes
  if (page === "project-emails") {
    return { page: "project-emails", tab: null, id: tabOrId || null };
  }

  // Handle model-building-manager routes
  if (page === "model-building-manager") {
    if (!tabOrId) {
      return { page: "model-building-manager", tab: "action-items", id: null };
    }

    // Check if tabOrId is a valid tab
    if (MODEL_BUILDING_MANAGER_TABS.includes(tabOrId)) {
      return { page: "model-building-manager", tab: tabOrId, id: null };
    } else {
      // Invalid tab, default to action-items
      return { page: "model-building-manager", tab: "action-items", id: null };
    }
  }

  // Handle other pages
  const pageName = ROUTE_PAGES[`/${page}`];
  if (pageName) {
    return { page: pageName, tab: null, id: null };
  }

  // Default fallback
  return { page: "projects", tab: "startup", id: null };
};

/**
 * Get the appropriate tab for a project based on its status
 */
export const getTabForProjectStatus = (modelingStatus) => {
  const statusToTabMap = {
    Startup: "startup",
    "Data Prep": "data-prep",
    Utilities: "utilities",
    "Ready to Model": "ready-to-model",
    Modeling: "modeling",
    "Double Check": "double-check",
    Corrections: "corrections",
    Review: "review",
    "Ready to Export": "ready-to-export",
    Send: "send",
    Completed: "completed",
  };

  return statusToTabMap[modelingStatus] || "startup";
};

/**
 * Handle legacy URL parameters and convert to new routing format
 */
export const handleLegacyUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const page = urlParams.get("page");
  const discrepancyId = urlParams.get("discrepancy");
  const tab = urlParams.get("tab");
  const projectId = urlParams.get("projectId") || urlParams.get("openProject");
  const exportId = urlParams.get("exportId");

  // Build new URL based on legacy parameters
  let newUrl = null;

  if (page === "discrepancies" && discrepancyId) {
    newUrl = generateDiscrepancyUrl(discrepancyId);
  } else if (tab || projectId) {
    newUrl = generateProjectModalUrl(tab || "startup", projectId);
    // Preserve exportId in the URL if present
    if (exportId && newUrl) {
      newUrl = `${newUrl}${newUrl.includes("?") ? "&" : "?"}exportId=${exportId}`;
    }
  } else if (page && PAGE_ROUTES[page]) {
    newUrl = generatePageUrl(page);
  }

  return newUrl;
};

/**
 * Clean URL by removing query parameters
 */
export const cleanUrl = () => {
  const cleanUrl = window.location.pathname;
  window.history.replaceState({}, "", cleanUrl);
};

/**
 * Generate a URL for planner config page
 */
export const generatePlannerConfigUrl = (
  sectionId = null,
  questionId = null,
) => {
  const baseUrl = "/planner-config";
  const params = new URLSearchParams();

  if (sectionId) {
    params.set("section", sectionId);
  }

  if (questionId) {
    params.set("question", questionId);
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Parse planner config URL parameters
 */
export const parsePlannerConfigUrl = (search) => {
  const params = new URLSearchParams(search);
  return {
    sectionId: params.get("section"),
    questionId: params.get("question"),
  };
};

/**
 * Navigate to a URL programmatically (to be used with React Router's navigate)
 */
export const createNavigationHelper = (navigate) => ({
  goToPage: (page) => navigate(generatePageUrl(page)),
  goToProjectTab: (tab) => navigate(generateProjectTabUrl(tab)),
  goToProject: (tab, projectId) =>
    navigate(generateProjectModalUrl(tab, projectId)),
  goToDiscrepancy: (discrepancyId) =>
    navigate(generateDiscrepancyUrl(discrepancyId)),
  goToProjectEmail: (emailId) => navigate(generateProjectEmailUrl(emailId)),
  goToPlannerConfig: (sectionId, questionId) =>
    navigate(generatePlannerConfigUrl(sectionId, questionId)),
  goBack: () => navigate(-1),
});
